/**
 * Cart Gift Code Manager - Moon Moon Agency
 * Codes promo "cadeau" : un code saisi dans le panier ajoute automatiquement un produit offert.
 *
 * Configuration (aucun dev necessaire) :
 * - Admin Shopify > Reductions : code "Achetez X, obtenez Y" (Y = produit offert a 100 %).
 *   C'est Shopify qui valide l'eligibilite (produit X au panier) et passe le cadeau a 0 €.
 * - Editeur de theme > MM Cart > bloc "Code cadeau" : code + produit offert (+ message si non eligible).
 *   Les blocs sont exposes en JSON dans [data-gift-code-offers].
 *
 * Le cadeau est ajoute AVANT d'appliquer le code : Shopify n'alloue une reduction Buy X Get Y
 * que si le produit Y est deja au panier (sinon le code ressort "applicable: false").
 * La ligne cadeau porte la property cachee _mm_gift_code = code.
 *
 * - apply(offer, cart) : appele par applyDiscount() (mm_cart.js). Ajoute le cadeau, applique le code,
 *   verifie que la reduction est allouee sur la ligne cadeau, sinon rollback atomique (cadeau + code).
 * - sync() : au chargement + apres chaque refresh du drawer :
 *   1. retire les cadeaux qui ne sont plus offerts (produit eligible retire, code retire au checkout...)
 *      ainsi que leur code ;
 *   2. ajoute le cadeau d'un code deja present au panier sans son cadeau (lien /discount/CODE).
 */

/*********************************************************************************************
 * 🎁 CART GIFT CODE MANAGER
 */
const CartGiftCodeManager = {
	config: {
		selectors: {
			offers:       '[data-gift-code-offers]',
			giftLine:     '[data-gift-code]',
			cartProducts: '[data-cart-products]',
		},
		property: '_mm_gift_code',
	},

	state: {
		busy: false,
		lastPendingSignature: '',
	},

	init() {
		document.addEventListener('cart:refreshed', () => this.sync());
		this.sync();
		window.log('info', '🎁 [CART_GIFT_CODE]', 'Initialized', { offers: this.getOffers().length });
	},

	/* Offres configurees via les blocs "Code cadeau" de la section MM Cart. */
	getOffers() {
		const el = document.querySelector(this.config.selectors.offers);
		if (!el) return [];
		try {
			return JSON.parse(el.textContent) || [];
		} catch (err) {
			window.log('error', '🎁 [CART_GIFT_CODE]', 'Invalid offers JSON', { error: err.message });
			return [];
		}
	},

	getOffer(code) {
		const normalized = code.trim().toLowerCase();
		return this.getOffers().find((offer) => offer.code.toLowerCase() === normalized) || null;
	},

	/* Line keys des cadeaux rattaches a un code (data-gift-code rendu par mm-cart-item). */
	getLineKeys(code) {
		const normalized = code.toLowerCase();
		return Array.from(document.querySelectorAll(this.config.selectors.giftLine))
			.filter((el) => el.dataset.giftCode.toLowerCase() === normalized)
			.map((el) => el.dataset.lineKey);
	},

	/**
	 * Ajoute le cadeau puis applique le code.
	 * @returns {Promise<{ok: boolean, reason?: string, data?: object}>}
	 *   data = reponse cart/update.js avec sections, a passer a mmRefreshCartUI.
	 *   reason : 'unavailable' (cadeau epuise) | 'not_eligible' | 'error'.
	 */
	async apply(offer, cart) {
		let result;
		this.state.busy = true;
		try {
			result = await this._apply(offer, cart);
		} catch (err) {
			window.log('error', '🎁 [CART_GIFT_CODE]', 'Apply failed', { code: offer.code, error: err.message });
			result = { ok: false, reason: 'error' };
		}
		this.state.busy = false;

		/* Erreur reseau en cours de route : nettoie un eventuel cadeau ajoute sans son code. */
		if (result.reason === 'error') this.sync();
		return result;
	},

	async _apply(offer, cart) {
		if (!this._findGiftLine(cart, offer.code)) {
			try {
				await this._post('cart/add.js', this._giftPayload(offer));
			} catch (err) {
				window.log('warn', '🎁 [CART_GIFT_CODE]', 'Gift not available', { code: offer.code, error: err.message });
				return { ok: false, reason: 'unavailable' };
			}
		}

		const data = await this._post('cart/update.js', this._withSections({ discount: offer.code }));
		const giftLine = this._findGiftLine(data, offer.code);
		if (giftLine && this._isGiftDiscounted(giftLine, offer.code)) {
			window.log('info', '🎁 [CART_GIFT_CODE]', 'Gift code applied', { code: offer.code });
			return { ok: true, data };
		}

		/* Conditions Shopify non remplies : rollback atomique (cadeau + code). */
		const rollback = { discount: '' };
		if (giftLine) rollback.updates = { [giftLine.key]: 0 };
		const rolledBack = await this._post('cart/update.js', this._withSections(rollback));
		window.log('info', '🎁 [CART_GIFT_CODE]', 'Not eligible, rolled back', { code: offer.code });
		return { ok: false, reason: 'not_eligible', data: rolledBack };
	},

	async sync() {
		if (this.state.busy) return;
		const cartCount = parseInt(document.querySelector(this.config.selectors.cartProducts)?.dataset.cartCount || '0', 10);
		const hasGiftLines = !!document.querySelector(this.config.selectors.giftLine);
		if (cartCount === 0 || (!hasGiftLines && !this.getOffers().length)) return;

		this.state.busy = true;
		try {
			const cart = await this._get('cart.js');
			if (!await this._removeStaleGifts(cart)) {
				await this._addPendingGift(cart);
			}
		} catch (err) {
			window.log('error', '🎁 [CART_GIFT_CODE]', 'Sync failed', { error: err.message });
		}
		/* Remis a false APRES le refresh : le cart:refreshed qu'il declenche ne relance pas de sync. */
		this.state.busy = false;
	},

	/* Cadeaux dont la reduction n'est plus allouee → retires avec leur code. */
	async _removeStaleGifts(cart) {
		const staleLines = cart.items.filter((item) => {
			const code = item.properties?.[this.config.property];
			return code && !this._isGiftDiscounted(item, code);
		});
		if (!staleLines.length) return false;

		const staleCodes = staleLines.map((item) => item.properties[this.config.property].toLowerCase());
		const body = { updates: Object.fromEntries(staleLines.map((item) => [item.key, 0])) };
		const codes = (cart.discount_codes || []).map((d) => d.code);
		const remainingCodes = codes.filter((code) => !staleCodes.includes(code.toLowerCase()));
		if (remainingCodes.length !== codes.length) body.discount = remainingCodes.join(',');

		const data = await this._post('cart/update.js', this._withSections(body));
		window.mmRefreshCartUI?.(data);
		window.log('info', '🎁 [CART_GIFT_CODE]', 'Stale gifts removed', { codes: staleCodes });
		return true;
	},

	/* Code cadeau present au panier sans son cadeau (lien /discount/CODE) → tentative d'ajout.
	 * Signature memorisee : pas de nouvelle tentative tant que le panier n'a pas change. */
	async _addPendingGift(cart) {
		const codes = (cart.discount_codes || []).map((d) => d.code.toLowerCase());
		const offer = this.getOffers().find((o) => codes.includes(o.code.toLowerCase()) && !this._findGiftLine(cart, o.code));
		if (!offer) return;

		const signature = `${offer.code}#${cart.items.map((item) => `${item.key}:${item.quantity}`).join('|')}`;
		if (signature === this.state.lastPendingSignature) return;
		this.state.lastPendingSignature = signature;

		await this._post('cart/add.js', this._giftPayload(offer));
		const after = await this._get('cart.js');
		const giftLine = this._findGiftLine(after, offer.code);
		if (!giftLine) return;

		if (this._isGiftDiscounted(giftLine, offer.code)) {
			const data = await this._post('cart/update.js', this._withSections({}));
			window.mmRefreshCartUI?.(data);
			window.log('info', '🎁 [CART_GIFT_CODE]', 'Pending gift added', { code: offer.code });
			return;
		}

		/* Pas encore eligible : on retire le cadeau (jamais affiche, pas de refresh) et on garde le code. */
		await this._post('cart/change.js', { id: giftLine.key, quantity: 0 });
	},

	/* ----- Helpers ----- */

	_giftPayload(offer) {
		return { items: [{ id: offer.variantId, quantity: 1, properties: { [this.config.property]: offer.code } }] };
	},

	_findGiftLine(cart, code) {
		const normalized = code.toLowerCase();
		return cart.items?.find((item) => item.properties?.[this.config.property]?.toLowerCase() === normalized) || null;
	},

	/* Le cadeau est offert si la reduction du code est allouee sur sa ligne. */
	_isGiftDiscounted(line, code) {
		const normalized = code.toLowerCase();
		return line.line_level_discount_allocations?.some((allocation) =>
			allocation.discount_application?.title?.toLowerCase() === normalized
		) || false;
	},

	_withSections(body) {
		const sectionId = window.mmGetCartSectionId?.() || '';
		if (sectionId) body.sections = sectionId;
		return body;
	},

	async _get(path) {
		return this._request(path, { method: 'GET', headers: { Accept: 'application/json' } });
	},

	async _post(path, body) {
		return this._request(path, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body),
		});
	},

	async _request(path, options) {
		const res = await fetch((window.Shopify?.routes?.root || '/') + path, options);
		const data = await res.json().catch(() => ({}));
		if (!res.ok) throw new Error(data.description || data.message || `HTTP ${res.status}`);
		return data;
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => CartGiftCodeManager.init());
} else {
	CartGiftCodeManager.init();
}

window.MMCartGiftCode = CartGiftCodeManager;
